import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UseFormReturn } from "react-hook-form";
import { FormData } from "../types";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface SellerInformationProps {
  form: UseFormReturn<FormData>;
}

export const SellerInformation = ({ form }: SellerInformationProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    const fetchBusinessProfile = async () => {
      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.user) return;

        const { data: profile, error } = await supabase
          .from('business_profiles')
          .select('*')
          .eq('user_id', session.session.user.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching business profile:', error);
          return;
        }

        if (profile) {
          // Update form fields with business profile data
          form.setValue('businessName', profile.business_name);
          form.setValue('businessTaxId', profile.business_tax_id);
          form.setValue('businessIban', profile.business_iban);
          form.setValue('businessSwift', profile.business_swift);
          form.setValue('businessEmail', profile.business_email);
          form.setValue('businessPhone', profile.business_phone);
          form.setValue('bankName', profile.bank_name); // Added this line to set the bank name
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };

    fetchBusinessProfile();
  }, [form]);

  return (
    <Card className="border-2 border-[#34375B]">
      <CardHeader className="p-3 md:p-6">
        <CardTitle className="text-[#34375B] text-lg md:text-xl">{t("seller_info.title", "Seller Information")}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 md:p-6 space-y-3 md:space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <FormField
            control={form.control}
            name="businessName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm md:text-base">{t("seller_info.business_name", "Business Name")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("seller_info.business_name_placeholder", "Happy Co")} className="placeholder:text-[#9397A8] bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base" {...field} />
                </FormControl>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="businessTaxId"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm md:text-base">{t("seller_info.tax_id", "Tax ID")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("seller_info.tax_id_number", "Tax ID number")} className="placeholder:text-[#9397A8] bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base" {...field} />
                </FormControl>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <FormField
            control={form.control}
            name="businessEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm md:text-base">{t("seller_info.email", "Email")}</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="contact@gmail.com" className="placeholder:text-[#9397A8] bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base" {...field} />
                </FormControl>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="businessPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm md:text-base">{t("seller_info.phone", "Phone")}</FormLabel>
                <FormControl>
                  <Input placeholder="+1234567890" className="placeholder:text-[#9397A8] bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base" {...field} />
                </FormControl>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          <FormField
            control={form.control}
            name="businessIban"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm md:text-base">{t("seller_info.iban", "IBAN")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("seller_info.iban", "IBAN number")} className="placeholder:text-[#9397A8] bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base" {...field} />
                </FormControl>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="businessSwift"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm md:text-base">{t("seller_info.swift_bic", "SWIFT/BIC")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("seller_info.swift_bic_code", "SWIFT/BIC code")} className="placeholder:text-[#9397A8] bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base" {...field} />
                </FormControl>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="bankName"
            render={({ field }) => (
              <FormItem className="sm:col-span-2 md:col-span-1">
                <FormLabel className="text-sm md:text-base">{t("seller_info.bank_name", "Bank Name")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("seller_info.bank_name_placeholder", "Bank name")} className="placeholder:text-[#9397A8] bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base" {...field} />
                </FormControl>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
};
